import React from 'react';
import styled from 'styled-components';
import { Button, formatHourMinutes, HourRangeField, HourTrack } from '@repo/design-system';
import type { HourTrackRange } from '@repo/design-system';
import { TagSwitcher, visuallyHidden } from '@repo/ui';
import type { AuthoredOpeningHoursDay } from '@repo/frontend-types';
import {
  brand,
  fontMono,
  ink,
  inkOn,
  radius,
  rgba,
  semantic,
  shadow,
  space,
  support,
  surface,
  typeRole,
  weight,
} from '@repo/design-tokens';

import {
  dayName,
  dayShort,
  midnightLine,
  secondRangeFor,
  toAuthoredDay,
  toTrackRanges,
  type DayState,
} from './hoursModel';
import { HOURS_CHANGED_ELSEWHERE } from './useOpeningHoursEditor';

/**
 * One day of the week editor (spec twin-details-page-hours-week, R7.5; frame 06): the day's name,
 * its Open / 24 hours / Closed switch, the design-system hour track, the times, and a sub line that
 * hangs below the times without growing the row (`ds-hour-track`'s steady rows, its R12a).
 *
 * It decides nothing about saving: every change is handed up as the whole day, and the week panel's
 * one gate (`commitWeek`) checks the week and saves it, or refuses and leaves this row as it was.
 */

export interface DayRowProps {
  day: AuthoredOpeningHoursDay;
  /** The selected row draws the frost edge and the track's handles (frame 06's Monday). */
  selected: boolean;
  /** A past calendar: everything shown, nothing editable. */
  readOnly: boolean;
  /** The ranges this day last had while open in this visit — restored when it is switched back to Open. */
  rememberedRanges: HourTrackRange[] | null;
  onSelect: () => void;
  onCommit: (next: AuthoredOpeningHoursDay) => void;
  /** The editor's count of answers it did not ask for (`useOpeningHoursEditor().revision`). */
  revision: number;
  /** Say, in the panel and its polite region, that a change was not made. */
  onRefused: (sentence: string) => void;
  /**
   * On a special period's week, what this day was in the standard it is compared with when the two
   * differ (*Was 06:00 – 22:00*; spec twin-details-page-hours-special, R5.2): the row is drawn in
   * lavender and the line leads its sub line. Null draws the row as on a standard week.
   */
  was?: string | null;
  className?: string;
}

const sameDay = (a: AuthoredOpeningHoursDay, b: AuthoredOpeningHoursDay) =>
  a.state === b.state && JSON.stringify(a.ranges) === JSON.stringify(b.ranges);
const sameRanges = (a: readonly HourTrackRange[], b: readonly HourTrackRange[]) =>
  a.length === b.length && a.every((r, i) => r.start === b[i].start && r.end === b[i].end);

/** A day newly opened with nothing to restore: 09:00 – 17:00. */
const DEFAULT_OPEN_RANGE: HourTrackRange = { start: 9 * 60, end: 17 * 60 };

const SWITCH_OPTIONS = [
  { value: 'ranges', text: 'Open' },
  { value: 'open_24h', text: '24 hours' },
  { value: 'closed', text: 'Closed' },
];

const DayRow: React.FC<DayRowProps> = ({
  day,
  selected,
  readOnly,
  rememberedRanges,
  onSelect,
  onCommit,
  revision,
  onRefused,
  was = null,
  className,
}) => {
  const name = dayName(day.dayOfWeek);
  const ranges = toTrackRanges(day);
  const commitRanges = (next: HourTrackRange[]) =>
    onCommit(toAuthoredDay(day.dayOfWeek, 'ranges', next));

  /*
   * The track's position while it moves. The track keeps its own preview after a commit until its
   * `ranges` change, so a refused commit — which changes nothing stored — would leave it showing the
   * refused position (R6.4: the row keeps its previous state). Feeding it the preview and clearing
   * that after every commit moves its `ranges` back to the stored week, which it then takes. Never
   * saved.
   */
  const [preview, setPreview] = React.useState<HourTrackRange[] | null>(null);
  /*
   * Cleared one render AFTER the commit, never in the same one: a click-to-jump calls onChange and
   * onCommit in a single event, and React would batch a same-event clear away, so the track would
   * never see the committed value and then the stored one.
   */
  const [settled, setSettled] = React.useState(0);
  React.useEffect(() => {
    if (settled > 0) setPreview(null);
  }, [settled]);
  const commitTrack = (next: HourTrackRange[]) => {
    setPreview(next);
    setSettled(n => n + 1);
    commitRanges(next);
  };
  // A move that comes back to the stored hours commits nothing, so it must leave no preview behind.
  const changeTrack = (next: HourTrackRange[]) =>
    setPreview(sameRanges(next, ranges) ? null : next);
  React.useEffect(() => {
    if (!selected || readOnly) setPreview(null);
  }, [selected, readOnly]);

  /** After *Add range* or a range's *Remove* (each takes itself away), where focus goes next. */
  const rowRef = React.useRef<HTMLDivElement>(null);
  const noRoomId = React.useId();
  const focusNext = React.useRef<HourTrackRange | 'add-range' | 'times' | null>(null);
  /** Bumped to remount this row's editors (below). */
  const [editors, setEditors] = React.useState(0);
  React.useEffect(() => {
    const want = focusNext.current;
    if (!want || !rowRef.current) return;
    focusNext.current = null;
    const fields = Array.from(rowRef.current.querySelectorAll<HTMLElement>('.times button'));
    const target =
      want === 'times'
        ? (fields[0] ?? rowRef.current.querySelector<HTMLElement>('.day-switch button'))
        : want === 'add-range'
          ? (rowRef.current.querySelector<HTMLElement>('.add-range') ?? fields[0])
          : // The new range's own field: it may sort before the first one.
            fields.find(b =>
              b
                .getAttribute('aria-label')
                ?.includes(`${formatHourMinutes(want.start)} to ${formatHourMinutes(want.end)}`),
            );
    target?.focus();
  }, [day, editors]);

  /*
   * A day's hours changed by an answer the editor did not ask for — a read bringing in someone
   * else's change — while this row's fields or track held a draft: those drafts were made against
   * the hours before, and committing one would put them back over the new hours with a fresh etag.
   * So the editors remount (a field closes, a drag ends, nothing is committed) and the person is
   * told. The person's own saves move the baseline without a remount.
   */
  const baseline = React.useRef({ revision, day });
  React.useEffect(() => {
    if (revision !== baseline.current.revision && !sameDay(baseline.current.day, day)) {
      const editing = !!rowRef.current?.contains(document.activeElement);
      setEditors(n => n + 1);
      setPreview(null);
      if (editing) {
        focusNext.current = 'times';
        onRefused(HOURS_CHANGED_ELSEWHERE);
      }
    }
    baseline.current = { revision, day };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, day]);

  const switchTo = (value: string) => {
    const state = value as DayState;
    if (state === day.state) return;
    if (state === 'ranges') {
      commitRanges(
        rememberedRanges && rememberedRanges.length > 0 ? rememberedRanges : [DEFAULT_OPEN_RANGE],
      );
      return;
    }
    onCommit(toAuthoredDay(day.dayOfWeek, state, []));
  };

  const open = day.state === 'ranges';
  const extra = open ? ranges.slice(1) : [];
  const midnight = open ? midnightLine(day.dayOfWeek, ranges) : null;
  const second = open && ranges.length === 1 ? secondRangeFor(ranges[0]) : null;
  const showAddRange = selected && open && ranges.length === 1 && !readOnly;

  return (
    <div
      ref={rowRef}
      className={[className, 'day-row', selected ? 'selected' : '', was ? 'special-changed' : '']
        .filter(Boolean)
        .join(' ')}
      data-testid={`day-row-${day.dayOfWeek}`}
      // Pressing anywhere on the row, or moving focus into it, makes it the selected row.
      onMouseDown={onSelect}
      onFocus={onSelect}
    >
      <span className="day-name" aria-hidden="true">
        {dayShort(day.dayOfWeek)}
      </span>
      <TagSwitcher
        className="day-switch"
        label={`${name} hours`}
        options={SWITCH_OPTIONS}
        selectedValue={day.state}
        onChange={switchTo}
        activation="manual"
        size="dense"
        disabled={readOnly}
      />
      <HourTrack
        key={`track-${editors}`}
        label={name}
        ranges={preview ?? ranges}
        allDay={day.state === 'open_24h'}
        active={selected}
        hue={was ? 'lavender' : undefined}
        onActivate={onSelect}
        disabled={readOnly}
        onChange={changeTrack}
        onCommit={commitTrack}
      />
      <div className="times">
        {open ? (
          ranges.length > 0 ? (
            <HourRangeField
              key={`times-${editors}`}
              label={name}
              ranges={ranges}
              index={0}
              disabled={readOnly}
              onCommit={commitRanges}
            />
          ) : (
            <span className="state-text">Open</span>
          )
        ) : day.state === 'open_24h' ? (
          <span className="state-text">24 hours</span>
        ) : (
          <span className="state-text">Closed</span>
        )}
        {(was || extra.length > 0 || midnight || showAddRange || day.state === 'open_24h') && (
          <div className="sub">
            {was && <span className="was">{was}</span>}
            {day.state === 'open_24h' && <span>Midnight to midnight</span>}
            {extra.map((r, i) => (
              // By position, not value: an edit must not remount the field it was typed into.
              <span className="extra" key={`${editors}-${i + 1}`}>
                <HourRangeField
                  label={name}
                  ranges={ranges}
                  index={i + 1}
                  size="sm"
                  disabled={readOnly}
                  onCommit={commitRanges}
                />
                {!readOnly && (
                  <Button
                    variant="text"
                    size="xs"
                    aria-label={`Remove ${formatHourMinutes(r.start)} – ${formatHourMinutes(r.end)} from ${name}`}
                    onClick={() => {
                      focusNext.current = 'add-range';
                      commitRanges(ranges.filter((_, j) => j !== i + 1));
                    }}
                  >
                    Remove
                  </Button>
                )}
              </span>
            ))}
            {midnight && <span className="closes">{midnight}</span>}
            {showAddRange && (
              <Button
                className="add-range"
                variant="text"
                size="xs"
                aria-label={`Add a second range to ${name}`}
                aria-disabled={second === null || undefined}
                aria-describedby={second === null ? noRoomId : undefined}
                title={second === null ? `No room for a second range on ${name}` : undefined}
                onClick={() => {
                  if (!second) return;
                  focusNext.current = second;
                  commitRanges([...ranges, second]);
                }}
              >
                Add range
              </Button>
            )}
            {showAddRange && second === null && (
              <span id={noRoomId} style={visuallyHidden}>
                {`No room for a second range on ${name}`}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/*
 * Frame 06's grid, measured by `ds-hour-track` (its R17): the day, the 252 px switch, the track, the
 * 150 px times — layout geometry, not brand values. Rows keep one height; the sub line hangs below
 * the times out of the row's flow (that spec's R12a).
 */
export const DAY_ROW_COLUMNS = '52px 252px minmax(0, 1fr) 150px';

/*
 * Beside the special period card (spec twin-details-page-hours-special, R6.8) the week is ~590 px at
 * 1440, and the 252 px switch column left the hour track 32 px. Frame 07 draws the switch at its own
 * width there; as built it measures 148 px (Open · 24 hours · Closed, dense — measured at 1440 × 900
 * by `openingHoursSpecial.ab.pw.spec.ts`; 156 keeps a 148 px switch from wrapping on subpixels), which gives the track ~128 px.
 */
export const DAY_ROW_COLUMNS_BESIDE_CARD = '52px 156px minmax(0, 1fr) 150px';

export default styled(DayRow)`
  display: grid;
  grid-template-columns: ${DAY_ROW_COLUMNS};
  align-items: center;
  gap: ${space[3]};
  padding: ${space[2]} ${space[3]} ${space[4]};
  border-radius: ${radius.lg};
  background: ${rgba(surface[0], 0.6)};
  border: 1.5px solid ${rgba(surface[0], 0.95)};
  box-shadow: 0 1px 2px rgba(${shadow.inkGlassRgb}, 0.06);

  &.selected {
    background: ${rgba(brand.frost, 0.55)};
    border-color: ${rgba(brand.cyan, 0.45)};
    /* Frame 06's selected row: the frost bloom and a soft ink drop (the fixture's values, on tokens). */
    box-shadow:
      0 0 12px 2px ${rgba(brand.frost, 0.9)},
      0 2px 6px rgba(${shadow.inkGlassRgb}, 0.08);
  }

  /*
   * A special period's changed day (frame 07, read off the bundle's day rows at 1:1): the lavender
   * wash at .18 over the row's usual white edge — the frame draws no lavender edge. The selected
   * row's frost wins, as it does in the frame.
   */
  &.special-changed:not(.selected) {
    background: ${rgba(support.lavender, 0.18)};
  }

  &:focus-within {
    outline: none;
  }

  .day-name {
    ${typeRole.body1Stronger};
    color: ${ink[1]};
  }

  .times {
    position: relative;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
  }

  /* Frame 06 sets the day's state in the time readout's mono. After the role, which brings its sans. */
  .state-text {
    ${typeRole.caption1};
    font-family: ${fontMono};
    font-weight: ${weight.medium};
    color: ${semantic.text.primary};
  }

  .sub {
    position: absolute;
    top: calc(100% - ${space[1]});
    right: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${space[1]};
    white-space: nowrap;
    ${typeRole.caption2};
    color: ${semantic.text.secondary};
  }

  .was {
    color: ${inkOn.lav};
  }

  /* Frame 06 draws the past-midnight close in aqua; at this size that is the on-aqua ink. */
  .closes {
    color: ${inkOn.aqua};
  }

  .extra {
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
  }
`;
